import { NotificationController } from 'core-components/library/popup'
import type { INetworkDebugRequest } from 'shared/lib/networkDebug'

export type TNetworkDataTab = 'request' | 'response' | 'mapped' | 'timing'

export type TNetworkRequestOrder = 'newest' | 'oldest' | 'slowest' | 'largest'

/** Pending requests have no duration or size yet, so they stand last when sorted by either. */
const COMPARE: Record<
  TNetworkRequestOrder,
  (a: INetworkDebugRequest, b: INetworkDebugRequest) => number
> = {
  newest: (a, b) => b.id - a.id,
  oldest: (a, b) => a.id - b.id,
  slowest: (a, b) => (b.durationMs ?? -1) - (a.durationMs ?? -1) || b.id - a.id,
  largest: (a, b) =>
    (b.jsonBytes ?? b.gzipBytes ?? -1) - (a.jsonBytes ?? a.gzipBytes ?? -1) || b.id - a.id,
}

export const sortNetworkRequests = (
  requests: readonly INetworkDebugRequest[],
  order: TNetworkRequestOrder
) => [...requests].sort(COMPARE[order])

export const isContainer = (value: unknown): value is Record<string, unknown> | unknown[] =>
  value !== null && typeof value === 'object'

export const formatBytes = (bytes: number) => {
  if (bytes >= 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  if (bytes >= 1024) {
    return `${Math.round(bytes / 1024)} KB`
  }

  return `${bytes} B`
}

/** How much of a pending request has downloaded, when its size is known. */
export const getProgressPercent = ({ progress }: INetworkDebugRequest) =>
  progress?.totalBytes ? Math.round((progress.loadedBytes / progress.totalBytes) * 100) : null

export const formatSeconds = (ms: number) => `${(ms / 1000).toFixed(2)} s`

export const formatClock = (timestamp: number) =>
  new Date(timestamp).toLocaleTimeString('en-GB', { hour12: false })

export const formatSize = ({ gzipBytes, jsonBytes }: INetworkDebugRequest) => {
  if (gzipBytes !== null && jsonBytes !== null) {
    return `${formatBytes(gzipBytes)} gzip → ${formatBytes(jsonBytes)}`
  }

  const bytes = jsonBytes ?? gzipBytes

  return bytes === null ? '' : formatBytes(bytes)
}

/** What the open tab shows, so Copy and Download take exactly that. */
export const getTabValue = (request: INetworkDebugRequest, tab: TNetworkDataTab) => {
  switch (tab) {
    case 'response':
      return request.raw
    case 'mapped':
      return request.mapped
    case 'timing':
      return request.timings
    default:
      return {
        method: request.method,
        url: request.url,
        body: request.body,
        headers: request.headers,
        storedIn: request.target,
        source: request.source,
        status: request.status,
      }
  }
}

export const getFileName = (request: INetworkDebugRequest, tab: TNetworkDataTab) => {
  const body = request.body as { fYear?: number; partnerId?: string } | null
  const parts = [request.name, tab, body?.fYear, body?.partnerId, formatClock(request.startedAt)]

  return `${parts
    .filter((part) => part !== undefined && part !== null && part !== '')
    .join('-')
    .replace(/:/g, '')}.json`
}

export const saveJson = (value: unknown, fileName: string) => {
  const url = URL.createObjectURL(
    new Blob([JSON.stringify(value, null, 2)], { type: 'application/json' })
  )
  const link = document.createElement('a')
  link.href = url
  link.download = fileName
  link.click()
  URL.revokeObjectURL(url)
}

export const copyText = (text: string, message: string) =>
  navigator.clipboard
    ?.writeText(text)
    .then(() => NotificationController.showSuccess({ message }))
    .catch(() => undefined)

export type TJsonCopyMode = 'value' | 'path' | 'keyValue'

/** What a tree node puts on the clipboard: an object or list as indented JSON, a string without
 *  quotes, its path, or the `"key": value` pair to paste into other JSON. */
export const getNodeCopyText = (value: unknown, mode: TJsonCopyMode, path: string, key: string) => {
  if (mode === 'path') {
    return path
  }

  const json = JSON.stringify(value, null, 2) ?? String(value)

  if (mode === 'keyValue') {
    return `${JSON.stringify(key)}: ${json}`
  }

  return typeof value === 'string' ? value : json
}

export const childPath = (parent: string, key: string | number, isArray: boolean) =>
  isArray ? `${parent}[${key}]` : `${parent}.${key}`

const MATCH_LIMIT = 4000

/** The paths whose key or value contains `query`, with every ancestor up to `rootPath`, so the
 *  tree can show only the branches that lead to a match. */
export const collectMatchPaths = (value: unknown, rootPath: string, query: string) => {
  const needle = query.toLowerCase()
  const paths = new Set<string>()
  let matches = 0

  const visit = (node: unknown, nodePath: string, ancestors: string[]) => {
    if (!isContainer(node) || matches >= MATCH_LIMIT) {
      return
    }

    const isArray = Array.isArray(node)
    const entries: [string, unknown][] = isArray
      ? node.map((item, index) => [String(index), item])
      : Object.entries(node)

    entries.forEach(([key, child]) => {
      const path = childPath(nodePath, key, isArray)
      const isHit =
        (!isArray && key.toLowerCase().includes(needle)) ||
        (!isContainer(child) && String(child).toLowerCase().includes(needle))

      if (isHit) {
        matches += 1
        paths.add(path)
        ancestors.forEach((ancestor) => paths.add(ancestor))
      }

      visit(child, path, [...ancestors, path])
    })
  }

  visit(value, rootPath, [rootPath])

  return paths
}

/** The first few plain fields of an object, shown beside it while it is collapsed. */
export const getPreview = (value: unknown) => {
  if (!isContainer(value) || Array.isArray(value)) {
    return ''
  }

  const parts: string[] = []

  for (const [key, field] of Object.entries(value)) {
    if (isContainer(field) || field === null || field === '') {
      continue
    }

    const text = typeof field === 'string' ? `"${truncate(field, 28)}"` : String(field)
    parts.push(`${key}: ${text}`)

    if (parts.length === 3) {
      break
    }
  }

  return parts.length ? `${parts.join(', ')}, …` : ''
}

export const truncate = (text: string, length: number) =>
  text.length > length ? `${text.slice(0, length)}…` : text
